import { RotateCcw } from 'lucide-react'
import { useFormulaStore } from '../formulaStore'
import { DEFAULT_FORMULAS } from '../formulaTypes'
import { FormulaCard } from './FormulaCard'
import { useAuthStore } from '@store/authStore'
import { useConfirm } from '@hooks/useConfirm'

export function FormulasTab() {
  const { user } = useAuthStore()
  const { getFormula, isFormulaCustom, saveFormula, resetFormula, resetAllFormulas } =
    useFormulaStore()
  const { confirm, ConfirmDialog } = useConfirm()

  const handleSave = (key: string, value: string) => {
    if (user) {
      saveFormula(key, value, { email: user.email, name: user.name })
    }
  }

  const handleReset = (key: string) => {
    if (user) {
      resetFormula(key, { email: user.email, name: user.name })
    }
  }

  const handleResetAll = async () => {
    const ok = await confirm({
      title: 'Reset all formulas',
      message: 'Are you sure you want to reset all formulas to their defaults? This cannot be undone.',
      confirmLabel: 'Reset All',
      variant: 'danger',
    })
    if (ok && user) {
      resetAllFormulas({ email: user.email, name: user.name })
    }
  }

  // Group formulas by category
  const keyMetricFormulas = Object.entries(DEFAULT_FORMULAS).filter(
    ([, f]) => f.category === 'key-metric'
  )
  const profitabilityFormulas = Object.entries(DEFAULT_FORMULAS).filter(
    ([, f]) => f.category === 'profitability'
  )
  const assessmentFormulas = Object.entries(DEFAULT_FORMULAS).filter(
    ([, f]) => f.category === 'assessment'
  )

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-[var(--text-primary)]">Formula Configuration</h3>
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            Customize the calculation formulas used in the Deal Calculator
          </p>
        </div>
        <button
          onClick={handleResetAll}
          className="flex items-center gap-2 px-3 py-2 text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
        >
          <RotateCcw size={14} />
          Reset All to Defaults
        </button>
      </div>

      {/* Key Metrics Section */}
      <div>
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>📊</span> Key Metrics Formulas
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {keyMetricFormulas.map(([key, formula]) => (
            <FormulaCard
              key={key}
              formulaKey={key}
              formula={formula}
              currentValue={getFormula(key)}
              isCustom={isFormulaCustom(key)}
              onSave={handleSave}
              onReset={handleReset}
            />
          ))}
        </div>
      </div>

      {/* Profitability Section */}
      <div>
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>💰</span> Profitability Formulas
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {profitabilityFormulas.map(([key, formula]) => (
            <FormulaCard
              key={key}
              formulaKey={key}
              formula={formula}
              currentValue={getFormula(key)}
              isCustom={isFormulaCustom(key)}
              onSave={handleSave}
              onReset={handleReset}
            />
          ))}
        </div>
      </div>

      {/* Deal Assessment Section */}
      <div>
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
          <span>🎯</span> Deal Assessment Formula
        </h4>
        <div className="grid grid-cols-1 gap-3">
          {assessmentFormulas.map(([key, formula]) => (
            <FormulaCard
              key={key}
              formulaKey={key}
              formula={formula}
              currentValue={getFormula(key)}
              isCustom={isFormulaCustom(key)}
              onSave={handleSave}
              onReset={handleReset}
            />
          ))}
        </div>
      </div>

      {/* Score Thresholds Info */}
      <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">Deal Assessment Thresholds</h4>
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="bg-success/10 rounded-lg p-3">
            <div className="text-success font-bold text-lg">≥252</div>
            <div className="text-[10px] text-success/70">Great Deal</div>
          </div>
          <div className="bg-info/10 rounded-lg p-3">
            <div className="text-info font-bold text-lg">≥140</div>
            <div className="text-[10px] text-info/70">Good Deal</div>
          </div>
          <div className="bg-warning/10 rounded-lg p-3">
            <div className="text-warning font-bold text-lg">≥60</div>
            <div className="text-[10px] text-warning/70">Average Deal</div>
          </div>
          <div className="bg-danger/10 rounded-lg p-3">
            <div className="text-danger font-bold text-lg">&lt;60</div>
            <div className="text-[10px] text-danger/70">Poor Deal</div>
          </div>
        </div>
      </div>
    </div>
  )
}
